import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row, Stack } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Controller, UseFormReturn } from "react-hook-form";
import { useLocation } from "react-router-dom";
import Select from "react-select";

import { getBracket } from "@/app/src/lib/api";
import classNames from "classnames";
import { subDays } from "date-fns";

import "../../../assets/css/majorybl/pages/clubhouse.scss";
import Loading from "../../../components/Loading";
import {
    selectLeague,
    selectSelectedTeam,
} from "../../../features/clubhouse/clubhouseSlice";
import {
    selectFields,
    selectSetting,
} from "../../../features/global/globalSlice";
import { useAppSelector } from "../../../hooks/reduxHooks";
import parseDate from "../../../lib/parseDate";
import { displayFormAlert } from "../../../lib/utils";
import { IGame, ILeague, IPlayoff, ISiteSetting } from "../../../types/api";
import useRescheduleGameHandlers from "../hooks/useRescheduleGameHandlers";
import { IRescheduleGameData } from "../types/reschedulegame";

const RescheduleGameForm = ({
    methods,
    game,
    schedule,
    setSchedule,
    leagueId,
}: {
    methods: UseFormReturn<IRescheduleGameData>;
    game: IGame;
    schedule: IGame[];
    setSchedule: React.Dispatch<React.SetStateAction<IGame[]>>;
    leagueId?: ILeague["id"];
}): React.JSX.Element => {
    const [playoffBracket, setPlayoffBracket] = useState<IPlayoff | undefined>(
        undefined,
    );
    const [sending, setSending] = useState<boolean>(false);

    const fields = useAppSelector(selectFields);
    const user = useAppSelector(state => state.auth.user);
    const selectedTeam = useAppSelector(selectSelectedTeam);
    const league = useAppSelector(selectLeague);
    const { setting: leagueStartSetting } = useAppSelector(state =>
        selectSetting(state, "majorybl_season_start_date"),
    ) as ISiteSetting;
    const { setting: leagueEndSetting } = useAppSelector(state =>
        selectSetting(state, "majorybl_season_end_date"),
    ) as ISiteSetting;
    const location = useLocation();

    const isOnLeagueManagementPage = /league-management/.test(
        location.pathname,
    );

    const maxDate: Date | undefined = useMemo(() => {
        if (game.isPlayoff === 0) {
            return parseDate(leagueEndSetting);
        } else {
            if (user!.userLevel <= 2) {
                return undefined;
            } else {
                if (!playoffBracket) {
                    // if the bracket isn't loaded, don't allow rescheduling into the future
                    return new Date(game.date);
                } else {
                    const bracket = game.champGameInfo
                        ? "championship"
                        : game.consGameInfo
                          ? "consolation"
                          : undefined;

                    // if we can't determine the bracket, don't allow rescheduling into the future
                    if (!bracket) return new Date(game.date);

                    const currentRound = playoffBracket.games[bracket]?.find(
                        pg => pg.gameId === game.id,
                    )?.round;

                    // if we can't determine the current round or the current round is the last round, don't allow rescheduling into the future
                    if (
                        !currentRound ||
                        currentRound === playoffBracket.rounds[bracket]
                    )
                        return new Date(game.date);

                    if (
                        league?.playoffBracket?.dates?.[bracket]?.find(
                            round =>
                                parseInt(round.round.toString()) ===
                                currentRound,
                        )?.allowReschedule === true
                    ) {
                        const nextRound = league.playoffBracket?.dates?.[
                            bracket
                        ].find(
                            round =>
                                parseInt(round.round.toString()) ===
                                currentRound + 1,
                        );

                        // Set the next round's date
                        const nextRoundDate = nextRound?.date
                            ? new Date(nextRound.date)
                            : null;

                        // If we have determined the next round's date, set the max date to that day minus 1, otherwise do not allow rescheduling into the future
                        return nextRoundDate
                            ? new Date(subDays(nextRoundDate, 1))
                            : new Date(game.date);
                    } else {
                        // If the current playoff round does not allow rescheduling, return the current game date as max date
                        return new Date(game.date);
                    }
                }
            }
        }
    }, [playoffBracket]);

    const minDate =
        new Date().getTime() < parseDate(leagueStartSetting).getTime()
            ? parseDate(leagueStartSetting)
            : new Date();

    const { onSubmit } = useRescheduleGameHandlers(
        isOnLeagueManagementPage,
        game,
        setSending,
        setSchedule,
        leagueId,
        selectedTeam?.id,
    );

    const watchField = methods.watch("field", {
        value: game.fieldId,
        label: game.fieldName,
    });

    const fieldDiamonds =
        watchField &&
        fields.find(f => parseInt(f.id.toString()) === watchField.value)
            ?.diamonds;

    let postponed: Date[] = [];
    let scheduled: Date[] = [];
    let rescheduled: Date[] = [];

    schedule
        .filter(g => new Date(g.date).getTime() > new Date().getTime())
        .forEach(g => {
            switch (g.status) {
                case "Scheduled":
                    scheduled.push(new Date(g.date));
                    break;
                case "Postponed":
                    postponed.push(new Date(g.date));
                    break;
                case "Rescheduled":
                    rescheduled.push(new Date(g.date));
                    break;
            }
        });

    useEffect(() => {
        const fetchBracket = async () => {
            if (!league) {
                setPlayoffBracket(undefined);
                return;
            }

            try {
                const response = await getBracket(league.id);
                setPlayoffBracket(response.bracket);
            } catch {
                setPlayoffBracket(undefined);
            }
        };

        fetchBracket();
    }, []);

    return (
        <Form
            onSubmit={methods.handleSubmit(onSubmit)}
            id="createGameForm"
            data-testid="createGameForm"
        >
            <Form.Control
                hidden
                {...methods.register("gameId")}
                value={game.id}
                readOnly
            />
            <Row>
                <Form.Group
                    controlId="datetime"
                    as={Col}
                    md={3}
                    data-testid="datetime"
                >
                    <Form.Label id="datetime">Date/Time *</Form.Label>
                    <Controller
                        control={methods.control}
                        name="datetime"
                        rules={{
                            required:
                                "Please select the date/time for the game",
                        }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <DatePicker
                                ariaLabelledBy="datetime"
                                selected={value}
                                showMonthDropdown
                                showYearDropdown
                                showTimeSelect
                                timeFormat="h:mm aa"
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="MMMM d, yyyy h:mm aa"
                                dropdownMode="select"
                                className="form-control"
                                onChange={onChange}
                                onBlur={onBlur}
                                minDate={minDate}
                                maxDate={maxDate}
                                minTime={new Date(0, 0, 0, 6, 0)}
                                maxTime={new Date(0, 0, 0, 21, 0)}
                                highlightDates={[
                                    {
                                        "react-datepicker__day--highlighted-postponed":
                                            postponed,
                                    },
                                    {
                                        "react-datepicker__day--highlighted-scheduled":
                                            scheduled,
                                    },
                                    {
                                        "react-datepicker__day--highlighted-rescheduled":
                                            rescheduled,
                                    },
                                    {
                                        "react-datepicker__day--selected": [
                                            new Date(game.date),
                                        ],
                                    },
                                ]}
                                id="datetime"
                            />
                        )}
                    />
                    {methods.formState.errors.datetime &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.datetime
                                .message as string,
                            settings: "mb-0",
                        })}
                </Form.Group>
                <Col className="dateLabels">
                    <h5>Highlighted Dates:</h5>
                    <Stack direction="horizontal" gap={2}>
                        <span className="react-datepicker__day--highlighted-scheduled">
                            Scheduled Game
                        </span>
                        <span className="react-datepicker__day--highlighted-rescheduled">
                            Rescheduled Game
                        </span>
                        <span className="react-datepicker__day--highlighted-postponed">
                            Postponed Game
                        </span>
                        <span className="react-datepicker__day--selected">
                            Selected Date
                        </span>
                    </Stack>
                </Col>
            </Row>
            <Row>
                <Form.Group
                    controlId="field"
                    as={Col}
                    md={6}
                    data-testid="field"
                >
                    <Form.Label id="field">Field *</Form.Label>
                    <Controller
                        name="field"
                        control={methods.control}
                        rules={{
                            required: "Please select a field",
                        }}
                        render={({ field }) => (
                            <Select
                                {...field}
                                aria-labelledby="field"
                                isSearchable={true}
                                options={fields
                                    .filter(f => f.id !== 532)
                                    .sort((a, b) => {
                                        return a.name.localeCompare(b.name);
                                    })
                                    .map(f => {
                                        return {
                                            value: parseInt(f.id.toString()),
                                            label:
                                                f.id === 512
                                                    ? f.name
                                                    : `${f.name} (${f.city}, ${f.state})`,
                                        };
                                    })}
                                isDisabled={
                                    isOnLeagueManagementPage
                                        ? false
                                        : game.isPlayoff
                                          ? selectedTeam?.id === game.home.id
                                              ? false
                                              : true
                                          : false
                                }
                            />
                        )}
                    />
                </Form.Group>
                <Form.Group controlId="note" as={Col} md={3} data-testid="note">
                    <Form.Label id="note">Field Note</Form.Label>
                    <Form.Control
                        type="text"
                        {...methods.register("note", {
                            maxLength: {
                                value: 255,
                                message:
                                    "Please limit the field note to 255 characters or less",
                            },
                        })}
                        aria-labelledby="note"
                        aria-invalid={
                            methods.formState.errors.note ? "true" : "false"
                        }
                        disabled={
                            isOnLeagueManagementPage
                                ? false
                                : game.isPlayoff
                                  ? selectedTeam?.id === game.home.id
                                      ? false
                                      : true
                                  : false
                        }
                    />
                    {methods.formState.errors.note &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.note
                                .message as string,
                        })}
                </Form.Group>
                {watchField && fieldDiamonds && fieldDiamonds.length > 0 && (
                    <Form.Group
                        controlId="diamond"
                        as={Col}
                        md={3}
                        data-testid="diamond"
                    >
                        <Form.Label id="diamond">Diamond #</Form.Label>
                        <Controller
                            name="diamond"
                            control={methods.control}
                            rules={{
                                required:
                                    "A diamond is required for this field",
                            }}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    aria-labelledby="diamond"
                                    isSearchable={false}
                                    options={fieldDiamonds.map(d => {
                                        return {
                                            value: parseInt(d.id.toString()),
                                            label: d.name,
                                        };
                                    })}
                                    isDisabled={
                                        isOnLeagueManagementPage
                                            ? false
                                            : game.isPlayoff
                                              ? selectedTeam?.id ===
                                                game.home.id
                                                  ? false
                                                  : true
                                              : false
                                    }
                                />
                            )}
                        />
                        {methods.formState.errors.diamond &&
                            displayFormAlert({
                                level: "danger",
                                text: methods.formState.errors.diamond
                                    .message as string,
                            })}
                    </Form.Group>
                )}
            </Row>
            <Row>
                <Col className={classNames("d-flex", "align-items-baseline")}>
                    <Button variant="primary" type="submit" disabled={sending}>
                        {user!.userLevel <= 2
                            ? "Reschedule Game"
                            : game.isPlayoff === 0
                              ? "Propose Reschedule"
                              : "Reschedule Game"}
                    </Button>
                    {sending && <Loading className="mx-3 w-auto" />}
                </Col>
            </Row>
        </Form>
    );
};

export default RescheduleGameForm;
